/** Wire types for opsCommand/getMarketingAttribution + connection status. */

export type MarketingCoverage = {
  inScope: number;
  mapped: number;
  unmapped: number;
  unmappedSample?: string[];
  partial?: boolean;
  sharedBrandNote?: boolean;
};

export type MarketingCampaign = {
  key: string;
  label: string;
  short: string;
  type: string;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  cpc: number;
  conversions: number;
  cpa: number;
  covers: number;
  netCovers: number;
  revenue: number;
  roas: number;
  netRoas: number;
  cancelRate: number;
  impressionShare: number;
  lostIsBudget: number;
  lostIsRank: number;
  assetRating?: string | null;
  qualityScore?: number | null;
  matched?: boolean;
};

export type MarketingChannel = {
  key: string;
  label: string;
  covers: number;
  reservations: number;
  coverShare: number;
  conversionRate: number;
  spend?: number | null;
  roas?: number | null;
};

export type MarketingDevice = {
  key: string;
  label: string;
  sessions: number;
  sessionShare: number;
  formCompletion: number;
  sessionToBooking: number;
};

export type MarketingAction = {
  id: string;
  category: string;
  source: 'google' | 'aleno';
  title: string;
  rationale: string;
  projectedImpact: string;
  confidence: string;
};

export type MarketingAttributionPayload = {
  status: string;
  message?: string;
  periodLabel: string;
  dateStart?: string;
  dateEnd?: string;
  coverage: MarketingCoverage;
  sourcesConnected: string[];
  months: Array<{
    label: string;
    sessions: number;
    widgetViews: number;
    bookingStarted: number;
    bookingCompleted: number;
    covers: number;
    adSpend: number;
    adClicks: number;
    attributedCovers: number;
    attributedRevenue: number;
  }>;
  channels: MarketingChannel[];
  campaigns: MarketingCampaign[];
  devices: MarketingDevice[];
  searchTerms: Array<{
    term: string;
    impressions: number;
    clicks: number;
    conversions: number;
    campaign: string;
  }>;
  demand: {
    impressionShare: number;
    lostToBudget: number;
    lostToRank: number;
    estMissedCovers: number;
    optimizationScore: number;
    pendingRecommendations: number;
  };
  gsc: {
    site: {
      clicks: number;
      impressions: number;
      ctr: number;
      position: number;
    } | null;
    topQueries: Array<{
      term: string;
      impressions: number;
      clicks: number;
    }>;
  };
  actions: MarketingAction[];
  funnelTotals: {
    sessions: number;
    widgetViews: number;
    bookingStarted: number;
    bookingCompleted: number;
    covers: number;
  };
  totals: {
    adSpend: number;
    adClicks: number;
    attributedCovers: number;
    attributedRevenue: number;
    avgRoas: number;
    avgNetRoas: number;
    cancelledCovers: number;
    avgCpa: number;
    modeledAvgCheck: number;
  };
};

export type MarketingDiscoveredResource = {
  externalId: string;
  displayName: string;
  kind: 'ga4' | 'ads' | 'gsc' | 'gbp';
  suggestedRestaurantIds?: string[];
  /** Matches this customer's venue names, or is already mapped here (WP1.8). */
  matchesCustomer?: boolean;
  /** The Google account it belongs to, for grouping. */
  accountId?: string;
  accountLabel?: string;
  /** Staff only: another customer already uses this property. */
  mappedElsewhere?: boolean;
  /**
   * The wizard's group (docs/19 section 5): belongs to this customer, looks
   * like other restaurants the person can open, or no restaurant found.
   */
  group?: 'here' | 'elsewhere' | 'none';
  /** Hints for "looks like": restaurants the person can open. Never ticked. */
  elsewhereSuggestions?: Array<{ id: string; name: string; city?: string }>;
  /** What the property feeds now, if anything. */
  feeds?: import('./googleAccounts').PropertyFeeds | null;
};

export type MarketingOutletOption = {
  id: string;
  name?: string;
  city?: string;
};

export const MARKETING_SOURCE_KINDS: Record<string, 'ga4' | 'ads' | 'gsc' | 'gbp'> = {
  ga: 'ga4',
  googleads: 'ads',
  gsc: 'gsc',
  gbp: 'gbp',
};

export function isMarketingSourceId(
  id: string,
): id is 'ga' | 'googleads' | 'gsc' | 'gbp' {
  return id === 'ga' || id === 'googleads' || id === 'gsc' || id === 'gbp';
}
